import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  GetUserGroupByIdResponse,
  GetUserGroupsPayload,
  GetUserGroupsResponse,
  GroupStatus,
  PostUserGroupPayload,
  PostUserGroupResponse,
  PutUserGroupPayload,
  PutUserGroupResponse
} from "@/types/groups";
import { CustomError } from "@/utils/errors";

import {
  deleteUserGroupApi,
  getUserGroupByIdApi,
  getUserGroupsApi,
  postUserGroup,
  putUserGroup
} from "./userGroupsApi";

export const GET_USER_GROUPS_DEFAULT_PER_PAGE = 99999;
export const GET_USER_GROUPS_DEFAULT_PARAMS = {
  query: null,
  status: null,
  order_by: null,
  sort_by: null,
  page: 1,
  per_page: GET_USER_GROUPS_DEFAULT_PER_PAGE
};

export const useGetInfiniteUserGroupsAPI = (
  {
    query,
    page,
    per_page
  }: GetUserGroupsPayload = GET_USER_GROUPS_DEFAULT_PARAMS,
  options?: Partial<
    UseInfiniteQueryOptions<
      GetUserGroupsResponse,
      Error,
      InfiniteData<GetUserGroupsResponse>
    >
  >
) =>
  useInfiniteQuery({
    queryKey: ["getInfiniteUserGroups", page, query],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getUserGroupsApi({
        query,
        status: GroupStatus.ACTIVE,
        page: pageParam as number,
        per_page
      }),
    getNextPageParam: (lastPage: GetUserGroupsResponse, allPages) => {
      if (lastPage?.data?.length < GET_USER_GROUPS_DEFAULT_PER_PAGE)
        return undefined;
      return allPages.length + 1;
    },
    ...options
  });

export const useGetUserGroupsAPI = (
  {
    query,
    status,
    order_by,
    sort_by,
    page,
    per_page
  }: GetUserGroupsPayload = GET_USER_GROUPS_DEFAULT_PARAMS,
  options?: Partial<
    UseQueryOptions<GetUserGroupsResponse, Error, GetUserGroupsResponse>
  >
) =>
  useQuery({
    queryKey: [
      "getUserGroups",
      query,
      status,
      order_by,
      sort_by,
      page,
      per_page
    ],
    queryFn: () =>
      getUserGroupsApi({
        query,
        status,
        order_by,
        sort_by,
        page,
        per_page
      }),
    ...options
  });

export const useDeleteUserGroupAPI = (
  options?: Partial<UseMutationOptions<void, CustomError, { id: string }>>
) =>
  useMutation({
    mutationFn: deleteUserGroupApi,
    onError: (error: CustomError) => {
      throw error;
    },
    ...options
  });

export const useGetUserGroupByIdAPI = (
  id: string,
  options?: Partial<
    UseQueryOptions<GetUserGroupByIdResponse, Error, GetUserGroupByIdResponse>
  >
) =>
  useQuery({
    queryKey: ["getUserGroupById", id],
    queryFn: () => getUserGroupByIdApi({ id }),
    ...options
  });

export const usePutUserGroupAPI = (
  options?: Partial<
    UseMutationOptions<PutUserGroupResponse, CustomError, PutUserGroupPayload>
  >
) =>
  useMutation({
    mutationKey: ["putUserGroup"],
    mutationFn: putUserGroup,
    ...options
  });

export const usePostUserGroupAPI = (
  options?: Partial<
    UseMutationOptions<PostUserGroupResponse, CustomError, PostUserGroupPayload>
  >
) =>
  useMutation({
    mutationKey: ["postUserGroup"],
    mutationFn: postUserGroup,
    ...options
  });
